import { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure worker locally using cdn
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PDFViewer({ url }) {
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.0);
    const [rotation, setRotation] = useState(0);

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
        setPageNumber(1);
    }

    // Reset state when url changes
    useEffect(() => {
        setPageNumber(1);
        setScale(1.0);
        setRotation(0);
    }, [url]);

    return (
        <div className="flex flex-col h-full bg-gray-100 rounded-xl overflow-hidden border border-gray-200">
            {/* Toolbar */}
            <div className="bg-white border-b border-gray-200 p-2 flex items-center justify-between shadow-sm z-10">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                        disabled={pageNumber <= 1}
                        className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-30 transition-colors text-gray-700"
                        title="Previous Page"
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <span className="text-sm font-medium text-gray-600 min-w-[3rem] text-center">
                        {pageNumber} / {numPages || '--'}
                    </span>
                    <button
                        onClick={() => setPageNumber(p => Math.min(numPages || p, p + 1))}
                        disabled={pageNumber >= numPages}
                        className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-30 transition-colors text-gray-700"
                        title="Next Page"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-700"
                        title="Zoom Out"
                    >
                        <ZoomOut size={18} />
                    </button>
                    <span className="text-xs font-medium text-gray-500 min-w-[3rem] text-center">
                        {Math.round(scale * 100)}%
                    </span>
                    <button
                        onClick={() => setScale(s => Math.min(2.5, s + 0.1))}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-700"
                        title="Zoom In"
                    >
                        <ZoomIn size={18} />
                    </button>
                </div>

                <div>
                    <button
                        onClick={() => setRotation(r => (r + 90) % 360)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-700"
                        title="Rotate"
                    >
                        <RotateCw size={18} />
                    </button>
                </div>
            </div>

            {/* PDF Content */}
            <div className="flex-1 overflow-auto flex justify-center bg-gray-50/50 p-4 relative">
                <div className="shadow-lg">
                    <Document
                        file={url}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={(error) => console.error("PDF Load Error:", error)}
                        loading={
                            <div className="flex items-center justify-center p-20">
                                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        }
                        error={(error) => (
                            <div className="flex flex-col items-center justify-center p-20 text-red-500 gap-2">
                                <p className="font-medium">Failed to load PDF</p>
                                <p className="text-xs text-gray-500 max-w-xs text-center">{error.message || "Unknown error"}</p>
                            </div>
                        )}
                        className="pdf-document"
                    >
                        <Page
                            pageNumber={pageNumber}
                            scale={scale}
                            rotate={rotation}
                            renderTextLayer={true}
                            renderAnnotationLayer={true}
                            className="bg-white"
                        />
                    </Document>
                </div>
            </div>
        </div>
    );
}
